import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Sparkles, TrendingDown } from "lucide-react";
import { formatBRL } from "../../lib/api";
import { loadQuote } from "../../lib/booking-storage";
import { useSEO } from "../../lib/seo";

function formatRange(startISO, endISO) {
  try {
    const s = new Date(startISO);
    const e = new Date(endISO);
    const d = s.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
    const h1 = s.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    const h2 = e.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    return { d, h1, h2 };
  } catch { return { d: "—", h1: "--:--", h2: "--:--" }; }
}

export default function ReservarCotacao() {
  const nav = useNavigate();
  const saved = loadQuote();

  useSEO({ title: "Sua reserva", description: "Confirme os detalhes da sua reserva." });

  useEffect(() => {
    if (!saved) nav("/espacos", { replace: true });
  }, [saved, nav]);

  if (!saved) return null;
  const { space, quote } = saved;
  const { d, h1, h2 } = formatRange(quote.start_time, quote.end_time);

  return (
    <div className="bg-gray-50 min-h-screen pb-12" data-testid="cotacao-page">
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={`/espacos/${space.id}`} className="flex items-center gap-2 text-sm font-semibold text-[#0A1128]" data-testid="back-to-space">
            <ArrowLeft size={18} /> Voltar
          </Link>
          <div className="text-xs uppercase tracking-widest text-gray-500">
            Etapa <span className="text-[#D4AF37] font-bold">1</span> de 3
          </div>
        </div>
        <div className="h-1 bg-gray-200">
          <div className="h-full bg-[#D4AF37]" style={{ width: "33%" }} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <header>
          <h1 className="font-display text-3xl text-[#0A1128] leading-tight">Sua reserva</h1>
          <p className="text-gray-600 mt-1">Revise antes de continuar.</p>
        </header>

        {/* Reservation summary */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden" data-testid="reservation-summary">
          {space.photo && (
            <div className="aspect-[3/1] overflow-hidden bg-gray-100">
              <img src={space.photo} alt={space.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-5 space-y-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">{space.category}</div>
              <div className="text-xl font-bold text-[#0A1128]">{space.name}</div>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-700">
              <span>📅 {d}</span>
              <span>⏰ {h1} às {h2}</span>
              <span>⏱ {quote.hours}h</span>
            </div>
            <div className="border-t border-gray-200 pt-3 flex items-baseline justify-between">
              <span className="text-sm font-semibold text-gray-600">Total</span>
              <span className="text-3xl font-black text-[#0A1128]">{formatBRL(quote.total_avulso)}</span>
            </div>
          </div>
        </div>

        {/* Upsell */}
        {quote.package_upsell && (
          <div className="bg-gradient-to-br from-[#D4AF37]/15 to-[#D4AF37]/5 rounded-2xl p-5 border-2 border-[#D4AF37]/40" data-testid="upsell-block">
            <div className="flex items-start gap-3">
              <Sparkles size={26} className="text-[#D4AF37] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-[#7a5f10] text-base">
                  Vai usar com frequência?
                </div>
                <p className="text-sm text-gray-700 mt-1.5 leading-relaxed">
                  {quote.package_upsell.message} O pacote tem {quote.package_upsell.package_hours}h
                  de uso, validade de {quote.package_upsell.validity_days} dias.
                </p>
                <div className="flex items-center gap-2 mt-3 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-md px-3 py-1.5 w-fit">
                  <TrendingDown size={14} /> Economia de {formatBRL(quote.package_upsell.economy_per_hour)}/hora
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTAs */}
        <div className={`grid gap-3 ${quote.package_upsell ? "sm:grid-cols-2" : "grid-cols-1"}`}>
          <button
            type="button"
            onClick={() => nav("/reservar/identificacao")}
            data-testid="cta-reservar-avulso"
            className="bg-[#0A1128] text-white rounded-xl py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#14213D] active:scale-[0.99] transition-all"
          >
            Reservar Avulso · {formatBRL(quote.total_avulso)}
          </button>
          {quote.package_upsell && (
            <Link
              to="/portal/comprar-pacote"
              data-testid="cta-comprar-pacote"
              className="bg-[#D4AF37] text-[#0A1128] rounded-xl py-4 font-bold uppercase tracking-wider text-sm text-center hover:brightness-95 active:scale-[0.99] transition-all"
            >
              Comprar Pacote · {formatBRL(quote.package_upsell.price_package)}
            </Link>
          )}
        </div>

        <div className="text-[11px] text-gray-500 text-center">
          Pagamento seguro · Cancelamento grátis até 24h antes do horário reservado
        </div>
      </div>
    </div>
  );
}
